// PanelOpenHandle — the drag-to-expand affordance on a COLLAPSED side panel's
// inner edge (the right dock's left edge, the left rail's right edge). Grab it
// and drag toward "wider" to pull the panel open (live, tracked 1:1 with the
// pointer); a plain click or Enter/Space opens it too — the keyboard/pointer
// path that does NOT require a drag, so the panel is still openable without the
// gesture. The existing far-edge switches (the chat panel's summon button / rail toggle) stay the
// primary click-to-open path; this is the physical pull-out in addition.
//
// Pure component: the impurity is confined to this handle's own pointer handlers
// (pointer capture per gesture, per-move clientX delta, tap-vs-drag by a small
// deadzone). The DECISION — the live width, and settle-open vs snap-back — is
// the host's PURE `panelDrag*` seam (app/panelDragOpen.ts). No buried effects;
// capture is released on pointer-up.
//
// growSign folds away which way is "wider", exactly as PanelResizeHandle does:
//   • rail (handle on its RIGHT edge): dragging → END (right, LTR) widens → +1;
//   • dock (handle on its LEFT edge): dragging → START widens → -1.
// so the reported `pullPx` is always signed "positive = wider" and the pure math
// never learns which panel it drives.

import { useRef } from "react";
import "./PanelOpenHandle.css";

// Below this much pointer travel a gesture is a TAP (open now), not a drag
// (pull-to-open). Keeps a click from being read as a zero-distance drag that
// would snap back short of the threshold.
const TAP_DEADZONE_PX = 6;

export type PanelOpenHandleProps = {
    // The accessible name (e.g. "Open the review panel" / "Expand the sidebar").
    readonly label: string;
    // +1 when dragging toward the END widens the panel (rail); -1 when dragging
    // toward the START widens it (dock).
    readonly growSign: 1 | -1;
    // Live pull while dragging — signed so positive = wider. The host renders
    // the preview width from it (panelDragWidth).
    readonly onDragMove: (pullPx: number) => void;
    // Gesture released after a real drag — the host settles (panelDragSettle):
    // open at the pulled width past the threshold, else snap back.
    readonly onDragEnd: (pullPx: number) => void;
    // Tap / Enter / Space — open the panel to its default slot without dragging.
    readonly onOpen: () => void;
    // The gesture ended WITHOUT a pointer-up — a `pointercancel` (a Mac trackpad
    // reinterpreting the drag as scroll/swipe/force-touch, the browser
    // interrupting) or lost pointer capture (incl. this handle unmounting
    // mid-gesture). The host must clear any in-flight pull to null WITHOUT
    // opening, so an interrupted drag can never leave `data-*-drag="opening"`
    // armed and the panel pinned open.
    readonly onDragCancel: () => void;
    readonly className?: string;
};

export const PanelOpenHandle = ({
    label,
    growSign,
    onDragMove,
    onDragEnd,
    onOpen,
    onDragCancel,
    className,
}: PanelOpenHandleProps) => {
    // The gesture's anchor (null between gestures) and whether it has moved past
    // the tap deadzone yet (a real drag vs a tap).
    const startX = useRef<number | null>(null);
    const draggedRef = useRef(false);

    // Signed pull ("positive = wider") from the anchor to the current pointer.
    const pullFrom = (clientX: number): number =>
        startX.current === null ? 0 : (clientX - startX.current) * growSign;

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        startX.current = e.clientX;
        draggedRef.current = false;
    };
    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (startX.current === null) return;
        if (Math.abs(e.clientX - startX.current) > TAP_DEADZONE_PX)
            draggedRef.current = true;
        // Below the deadzone this is still a candidate tap: publish no preview
        // pull at all (not even 0 — the host arms data-*-drag="opening" from
        // "pull !== null", so a tap must leave the pull untouched/null) so the
        // closed-state CSS keeps winning until a real drag is confirmed.
        if (draggedRef.current) onDragMove(pullFrom(e.clientX));
    };
    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (startX.current === null) return;
        const pull = pullFrom(e.clientX);
        const dragged = draggedRef.current;
        startX.current = null;
        draggedRef.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
        // A tap (no real travel) opens immediately; a real drag hands the final
        // pull to the host to settle open-or-snap-back.
        if (dragged) onDragEnd(pull);
        else onOpen();
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
        }
    };
    // A gesture that ends by any path OTHER than pointer-up — pointercancel, or
    // lost pointer capture (which also fires if this handle unmounts mid-drag).
    // Reset the gesture and tell the host to drop the in-flight pull, so the
    // preview width never survives as a stuck pin. Guarded on `startX` so it is
    // a no-op after a normal pointer-up already cleared the gesture (releasing
    // capture there fires `lostpointercapture` too).
    const cancelGesture = () => {
        if (startX.current === null) return;
        startX.current = null;
        draggedRef.current = false;
        onDragCancel();
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: a positioned drag affordance layered over the panel edge; role="button" + tabIndex + the Enter/Space path IS its keyboard semantics (a native <button> cannot host the pointer-drag gesture without also firing a click after every drag).
        <div
            role="button"
            aria-label={label}
            tabIndex={0}
            title={label}
            className={`bilda-panel-open-handle${className ? ` ${className}` : ""}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={cancelGesture}
            onLostPointerCapture={cancelGesture}
            onKeyDown={onKeyDown}
        />
    );
};
